import { db } from './firebase.js'
import { query, collection, collectionGroup, orderBy, getDocs, getDoc, setDoc, updateDoc, addDoc, limit, startAfter, startAt, doc , where, or, and,  getCountFromServer, getAggregateFromServer, sum, deleteDoc, increment, deleteField } from "firebase/firestore";
import { formatCurrentTime } from './components/sharedUtils';

export const mapProductDoc = (snap, { customer, override, excludeId }) => {
  const docId = snap.id;
  const tempData = snap.data() || {};
  if (!tempData) return null;

  // filter hidden/excluded (same logic you had, but use excludeId instead of window.href)
  const hidden = !(tempData.hideFromMarketplace == null || tempData.hideFromMarketplace === false || customer != null || override);
  const isDemoBlocked = tempData.customerId === "siteDemo" && !customer;
  if ((excludeId && excludeId === docId) || hidden || isDemoBlocked) return null;

  const results = tempData.editedResults || tempData.chatGptResponse || {};
  results.noBgImageUrls = tempData.noBgImageUrls || [];
  results.thumbnailUrls = [];
  results.productUri = docId;

  // price
  if (tempData.isFree) {
    results.price = "FREE";
  } else if (typeof results.price === "string") {
    results.price = "$" + results.price.replace("$", "");
  } else {
    results.price = "Unknown";
  }

  // images
  // Raw, unfiltered mainImages (preserves order + isHidden flags) so the UI can
  // offer reorder/delete without losing track of hidden entries.
  results.mainImages = tempData.mainImages || [];
  if (tempData.mainImages) {
    tempData.mainImages.forEach((image) => {
      if (!image.isHidden) results.thumbnailUrls.push(image.urlString);
    });
    if (results.thumbnailUrls.length === 0) {
      tempData.mainImages.forEach((image) => {
        if (image.isHidden) results.thumbnailUrls.push(image.urlString);
      });
    }
    if (results.thumbnailUrls.length > 0) {
      results.mainImage = results.thumbnailUrls[0];
    }
  } else if (tempData.noBgImageUrls?.length > 0) {
    results.mainImage = tempData.noBgImageUrls[0];
    results.thumbnailUrls = tempData.thumbnailUrls || [];
  } else if (tempData.originalImageUrls?.length > 0) {
    // CV pipeline hasn't finished background removal yet (mainImages/noBgImageUrls
    // aren't written until every expected image is done), but the original upload is
    // written to the doc immediately — use it so the card shows a real photo
    // right away instead of a spinner for the duration of CV processing.
    results.mainImage = tempData.originalImageUrls[0];
    results.thumbnailUrls = tempData.originalImageUrls;
  } else {
    results.thumbnailUrls = tempData.thumbnailUrls || [];
  }

  // metadata
  results.sku = tempData.sku || null;
  results.created = tempData.created || null;
  results.updated = tempData.updated || null;
  results.ebayData = tempData.eBayArray || null;
  results.shopifyData = tempData.shopifyArray || null;
  results.squareData = tempData.squareArray || null;
  results.googleMerchantData = tempData.googleMerchantArray || null;
  results.etsyData = tempData.etsyArray || null;
  results.etsyTaxonomyId = tempData.etsyTaxonomyId || null;
  results.upc = tempData.upc || null;
  results.location = tempData.location || null;
  results.fieldsRequired = tempData.fieldsRequired || null;
  results.fieldsOptional = tempData.fieldsOptional || null;
  // Sale tracking (INTEGRATION_SPEC.md "Sale tracking fields")
  results.totalSold = tempData.totalSold ?? null;
  results.totalRevenue = tempData.totalRevenue ?? null;
  results.totalCost = tempData.totalCost ?? null;
  results.lastSaleChannel = tempData.lastSaleChannel || null;
  results.lastSaleAt = tempData.lastSaleAt || null;
  results.lastSalePrice = tempData.lastSalePrice ?? null;
  results.tags = tempData.tags || [];
  results.notes = tempData.notes || '';
  results.quantity = tempData.quantity != null ? tempData.quantity : 1;
  results.productReady = tempData.productReady || false;
  results.finishedProcessing = tempData.finishedProcessing || false;
  results.needsImages = tempData.needsImages || false;
  results.lastRetry = tempData.lastRetry || null;
  results.importedFromSquare = tempData.importedFromSquare || false;
  results.smartsheetRowId = tempData.smartsheetRowId || null;
  results.shopifyBodyHtml = tempData.shopifyBodyHtml || null;
  results.gptDimensionsData = tempData.gptDimensionsData || null;
  results.priceOptions = tempData.priceOptions || null;
  results.shopifyCategory = tempData.shopifyCategory || null;
  results.gptCategories = tempData.gptCategories || null;
  results.overallConfidence = tempData.overallConfidence ?? null;
  // Lands in Firestore well before consolidatedData/gptCategories/priceOptions
  // (written by cv_pipeline once the CV "done" threshold is hit), so the product
  // list can show a real title before the rest of finalization completes.
  results.validatedTitle = tempData.validatedTitle || null;

  // consolidated overrides
  if (tempData.consolidatedData) {
    const c = tempData.consolidatedData;
    if (c["Title"]) results.title = c["Title"];
    if (c["Description"]) results.description = c["Description"];
    if (c["Price"]) results.price = "$" + c["Price"].replace("$", "");
    if (c["SKU"]) results.sku = c["SKU"];
    results.specs = c;
    results.allImages = [];
    for (const image of (tempData.mainImages || [])) {
        if(image.isHidden) { continue }
        results.allImages.push(image)
    }
  }

  return results;
};

export const getProducts = async (
  passedLimit,
  lastSnap,
  customer,
  searchString,
  completion,
  override = false,
  // NEW optional params:
  { productUri = null, excludeId = null, sortDir = 'desc' } = {}
) => {
  try {
    // ---- Single product mode ----
    if (productUri) {
      const ref = customer
        ? doc(db, `customers/${customer}/products/${productUri}`)
        : doc(db, "products", productUri);

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        completion([], null);
        return;
      }

      const mapped = mapProductDoc(snap, { customer, override, excludeId });
      completion(mapped ? [mapped] : [], null);
      return;
    }

    // ---- List mode (your original logic) ----
    let productsQuery;
    if (customer) {
      if (searchString) {
        productsQuery = searchCustomerProducts(
          passedLimit,
          lastSnap,
          customer,
          searchString.toLowerCase(),
          override
        );
      } else {
        productsQuery = query(
          collection(db, `customers/${customer}/products`),
          orderBy("created", sortDir),
          ...(lastSnap ? [startAfter(lastSnap)] : []),
          limit(passedLimit)
        );
      }
    } else {
      if (searchString) {
        productsQuery = searchProducts(
          passedLimit,
          lastSnap,
          searchString.toLowerCase(),
          override
        );
      } else {
        productsQuery = query(
          collection(db, "products"),
          orderBy("created", "desc"),
          ...(lastSnap ? [startAfter(lastSnap)] : []),
          limit(passedLimit)
        );
      }
    }

    const querySnapshot = await getDocs(productsQuery);
    const list = [];
    querySnapshot.forEach((snap) => {
      const mapped = mapProductDoc(snap, { customer, override, excludeId });
      if (mapped) list.push(mapped);
    });

    const lastItem = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    completion(list, lastItem);
  } catch (err) {
    console.error("Error fetching products:", err);
    completion([], null);
  }
};

export const getProductCount = async (customer) => {
  try {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customer);
    const prodColRef = collection(custDocRef, "products");

    // Use Firestore's count aggregation
    const snapshot = await getCountFromServer(query(prodColRef));
    return snapshot.data().count; // Retrieve the count value
  } catch (error) {
    console.error("Error fetching product count:", error);
    throw error;
  }
};


export const deleteProduct = async (customer, product) => {
  try {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customer);

    // product references
    const prodColRef = collection(custDocRef, "products");
    const prodRef = doc(prodColRef, product);

    // deletedProducts references
    const deletedColRef = collection(custDocRef, "deletedProducts");
    const deletedRef = doc(deletedColRef, product);

    // get product data
    const snapshot = await getDoc(prodRef);
    if (!snapshot.exists()) {
      throw new Error(`Product ${product} not found for customer ${customer}`);
    }

    // copy into deletedProducts (include a deletedAt timestamp for tracking)
    await setDoc(deletedRef, {
      ...snapshot.data(),
      deletedAt: new Date().toISOString()
    });

    // now delete original
    await deleteDoc(prodRef);

  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

export const saveProductQuantity = async (customer, product, quantity) => {
  try {
    const prodRef = doc(collection(doc(collection(db, 'customers'), customer), 'products'), product);
    await setDoc(prodRef, { quantity: Number(quantity) }, { merge: true });
  } catch (error) {
    console.error('Error saving product quantity:', error);
    throw error;
  }
};

export const saveProductDimensions = async (customer, product, dimensions) => {
  try {
    const prodRef = doc(collection(doc(collection(db, 'customers'), customer), 'products'), product);
    await setDoc(prodRef, { gptDimensionsData: dimensions }, { merge: true });
  } catch (error) {
    console.error('Error saving product dimensions:', error);
    throw error;
  }
};

export const saveProductSpecs = async (customer, product, specs) => {
  try {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customer);
    const prodColRef = collection(custDocRef, "products");
    const prodRef = doc(prodColRef, product);

    
    await setDoc(prodRef, { 'consolidatedData': specs }, { merge: true });
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

export const saveProductShopifyCategory = async (customer, productUri, categoryData) => {
  try {
    const prodRef = doc(collection(doc(collection(db, 'customers'), customer), 'products'), productUri);
    await setDoc(prodRef, { shopifyCategory: categoryData }, { merge: true });
  } catch (error) {
    console.error('Error saving shopify category:', error);
    throw error;
  }
};

export const saveProductEbayCategory = async (customer, productUri, primaryCategory, existingGptCategories) => {
  try {
    const prodRef = doc(collection(doc(collection(db, 'customers'), customer), 'products'), productUri);
    const updated = { ...(existingGptCategories || {}), primaryCategory };
    await setDoc(prodRef, { gptCategories: updated }, { merge: true });
  } catch (error) {
    console.error('Error saving ebay category:', error);
    throw error;
  }
};

export const saveProductImages = async (customer, product, images) => {
  try {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customer);
    const prodColRef = collection(custDocRef, "products");
    const prodRef = doc(prodColRef, product);
    
    await setDoc(prodRef, { 'mainImages': images }, { merge: true });
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};
export const getApiKey = async (customer) => {
  try {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customer);
   
    const custDocSnap = await getDoc(custDocRef);

    // Check if document exists and return the API key
    if (custDocSnap.exists()) {
      return custDocSnap.data().apiKey;
    } else {
      console.error("No such customer document found.");
      throw new Error("Customer document does not exist.");
    }
    } catch (error) {
    console.error("Error fetching API key:", error);
    throw error;
  }
};

const fetchProducts = (passedLimit, lastSnap, override=false) => {
  const customerFilter =  or(where('customerId', '==', 'A77eZv8Ku1h5Ohn68cFarpjNrkk1'),
  where('customerId', '==', 'ntjs7Ybyd1XibATjUN3xVFCDQsa2'))
  const filter = (db.app.options.projectId == 'shopik-stage' || override) ? where('finishedProcessing', '==', true) :  and(where('finishedProcessing', '==', true), where('hideFromMarketplace', '==', false), customerFilter)
  return lastSnap
    ? query(
        collectionGroup(db, 'products'),
        filter,
        orderBy('created', 'desc'),
        startAfter(lastSnap),
        limit(passedLimit)
      )
    : query(collectionGroup(db, 'products'), 
        filter,
        orderBy('created', 'desc'), 
        limit(passedLimit));
};

export const fetchCustomerProducts = (limitCount, customer, lastSnap, override) => {
  const prodColRef = collection(db, `customers/${customer}/products`);
  const filter = where('finishedProcessing', '==', true)
  if (lastSnap) {
    return query(prodColRef, filter, orderBy("created"), startAfter(lastSnap), limit(limitCount));
  }
  return query(prodColRef, filter, orderBy("created"), limit(limitCount));
};

const searchProducts = (passedLimit, lastSnap, searchString, override=false) => {
  const filter = (db.app.options.projectId == 'shopik-stage' || override) ? and(where('finishedProcessing', '==', true), where('searchableText', 'array-contains', searchString)) : and(where('finishedProcessing', '==', true), where('hideFromMarketplace', '==', false), where('searchableText', 'array-contains', searchString))
  return lastSnap
    ? query(
        collectionGroup(db, 'products'),
        filter,
        orderBy('created', 'desc'),
        startAfter(lastSnap),
        limit(passedLimit)
      )
    : query(collectionGroup(db, 'products'), 
        filter,
        orderBy('created', 'desc'), 
        limit(passedLimit));
};

// ─── Category Config (per-customer, per-category column + Body HTML settings) ──

export const getCategoryConfig = async (customerId, categoryKey) => {
  try {
    const ref = doc(db, `customers/${customerId}/categoryConfigs/${categoryKey}`);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error('Error fetching category config:', error);
    return null;
  }
};

export const saveCategoryConfig = async (customerId, categoryKey, config) => {
  try {
    const ref = doc(db, `customers/${customerId}/categoryConfigs/${categoryKey}`);
    await setDoc(ref, config);
  } catch (error) {
    console.error('Error saving category config:', error);
    throw error;
  }
};

export const deleteCategoryConfig = async (customerId, categoryKey) => {
  try {
    const ref = doc(db, `customers/${customerId}/categoryConfigs/${categoryKey}`);
    await deleteDoc(ref);
  } catch (error) {
    console.error('Error deleting category config:', error);
    throw error;
  }
};

export const saveBodyHtml = async (customerId, productUri, html) => {
  try {
    const ref = doc(db, `customers/${customerId}/products/${productUri}`);
    await setDoc(ref, { shopifyBodyHtml: html }, { merge: true });
  } catch (error) {
    console.error('Error saving body HTML:', error);
    throw error;
  }
};

// Merges arbitrary top-level product fields (sku, upc, location, tags, notes, …)
// and stamps `updated`, same as the iOS app's publishChanges.
export const saveProductFields = async (customerId, productUri, fields) => {
  const ref = doc(db, `customers/${customerId}/products/${productUri}`);
  await setDoc(ref, { ...fields, updated: formatCurrentTime() }, { merge: true });
};

// True when no *other* product of this customer already uses the SKU.
export const isSkuUnique = async (customerId, sku, productUri) => {
  try {
    const snap = await getDocs(query(
      collection(db, `customers/${customerId}/products`),
      where('sku', '==', sku),
      limit(2),
    ));
    return snap.docs.every((d) => d.id === productUri);
  } catch (error) {
    // Don't block a save on a failed lookup — matches iOS isSkuUnique.
    console.error('Error checking SKU uniqueness:', error);
    return true;
  }
};

export const getCustomerDoc = async (customerId) => {
  const snap = await getDoc(doc(db, `customers/${customerId}`));
  return snap.exists() ? snap.data() : null;
};

// ─── Sales (INTEGRATION_SPEC.md "Sale tracking fields") ───────────────────────

const productRef = (customerId, productUri) => doc(db, `customers/${customerId}/products/${productUri}`);
const saleHistoryCol = (customerId, productUri) => collection(db, `customers/${customerId}/products/${productUri}/saleHistory`);

// Manual sale entry — writes the same fields the backend's apply_sale writes for
// a detected sale (mirrors ProductDataModel.markAsSold on iOS). Returns the new
// quantity so the caller can delist when it hits zero.
export const recordManualSale = async (customerId, product, {
  platform, quantitySold, unitPrice, unitCost, soldAt, orderId, notes,
}) => {
  const currentQty = product.quantity ?? 1;
  if (currentQty <= 0) throw new Error('This item has no remaining quantity to sell.');
  const newQuantity = Math.max(0, currentQty - quantitySold);
  const lineTotal = unitPrice != null ? unitPrice * quantitySold : null;
  const lineCost = unitCost != null ? unitCost * quantitySold : null;

  const update = {
    quantity: newQuantity,
    totalSold: increment(quantitySold),
    lastSaleChannel: platform,
    lastSaleAt: soldAt,
  };
  if (unitPrice != null) {
    update.lastSalePrice = unitPrice;
    update.totalRevenue = increment(lineTotal);
  }
  if (lineCost != null) update.totalCost = increment(lineCost);
  await updateDoc(productRef(customerId, product.productUri), update);

  const history = { channel: platform, quantitySold, unitPrice, lineTotal, unitCost, lineCost, soldAt };
  if (orderId) history.orderId = orderId;
  if (notes) history.notes = notes;
  await addDoc(saleHistoryCol(customerId, product.productUri), history);

  return {
    quantity: newQuantity,
    totalSold: (product.totalSold || 0) + quantitySold,
    lastSaleChannel: platform,
    lastSaleAt: soldAt,
    ...(unitPrice != null && { lastSalePrice: unitPrice, totalRevenue: (product.totalRevenue || 0) + lineTotal }),
    ...(lineCost != null && { totalCost: (product.totalCost || 0) + lineCost }),
  };
};

// Raw saleHistory entries, newest first. `soldAt` is a Timestamp on entries
// apply_sale writes but a formatted string on manual ones, so sort client-side.
export const getSaleHistory = async (customerId, productUri, parseDate) => {
  const snap = await getDocs(saleHistoryCol(customerId, productUri));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .map((e) => ({ ...e, soldAtDate: parseDate(e.soldAt) }))
    .sort((a, b) => (b.soldAtDate?.getTime() || 0) - (a.soldAtDate?.getTime() || 0));
};

// Edits a sale's price/cost and rolls the deltas into the product totals
// (same delta approach as iOS SaleHistoryView.saveSaleDetails).
export const updateSaleEntry = async (customerId, productUri, entry, { unitPrice, unitCost }, isMostRecent) => {
  const qty = entry.quantitySold || 0;
  const lineTotal = unitPrice != null ? unitPrice * qty : null;
  const lineCost = unitCost != null ? unitCost * qty : null;
  const revenueDelta = (lineTotal || 0) - (entry.lineTotal || 0);
  const costDelta = (lineCost || 0) - (entry.lineCost || 0);

  await updateDoc(doc(saleHistoryCol(customerId, productUri), entry.id), { unitPrice, lineTotal, unitCost, lineCost });

  const productUpdate = {};
  if (revenueDelta !== 0) productUpdate.totalRevenue = increment(revenueDelta);
  if (costDelta !== 0) productUpdate.totalCost = increment(costDelta);
  if (isMostRecent) productUpdate.lastSalePrice = unitPrice != null ? unitPrice : deleteField();
  if (Object.keys(productUpdate).length) await updateDoc(productRef(customerId, productUri), productUpdate);

  return { ...entry, unitPrice, lineTotal, unitCost, lineCost };
};

// Deletes an off-platform sale and reverses it on the product. Channel-detected
// sales have a real order behind them, so only manual ones can be deleted.
export const deleteSaleEntry = async (customerId, productUri, entry, remainingEntries, formatDate) => {
  const qty = entry.quantitySold || 0;
  const productUpdate = {
    quantity: increment(qty),
    totalSold: increment(-qty),
  };
  if (entry.lineTotal != null) productUpdate.totalRevenue = increment(-entry.lineTotal);
  if (entry.lineCost != null) productUpdate.totalCost = increment(-entry.lineCost);
  const next = remainingEntries[0];
  if (next) {
    productUpdate.lastSaleChannel = next.channel || deleteField();
    productUpdate.lastSaleAt = next.soldAtDate ? formatDate(next.soldAtDate) : deleteField();
    productUpdate.lastSalePrice = next.unitPrice != null ? next.unitPrice : deleteField();
  } else {
    productUpdate.lastSaleChannel = deleteField();
    productUpdate.lastSaleAt = deleteField();
    productUpdate.lastSalePrice = deleteField();
  }
  await deleteDoc(doc(saleHistoryCol(customerId, productUri), entry.id));
  await updateDoc(productRef(customerId, productUri), productUpdate);
};

// Every product that has sold at least once. Filtered server-side on
// totalSold > 0 so load time scales with sales, not catalog size.
export const getSoldProducts = async (customerId) => {
  const results = [];
  let last = null;
  const pageLimit = 100;
  for (;;) {
    const snap = await getDocs(query(
      collection(db, `customers/${customerId}/products`),
      where('totalSold', '>', 0),
      orderBy('totalSold'),
      ...(last ? [startAfter(last)] : []),
      limit(pageLimit),
    ));
    snap.forEach((s) => {
      const mapped = mapProductDoc(s, { customer: customerId });
      if (mapped) results.push(mapped);
    });
    if (snap.docs.length < pageLimit) break;
    last = snap.docs[snap.docs.length - 1];
  }
  return results;
};

export const getSalesTotals = async (customerId) => {
  const col = collection(db, `customers/${customerId}/products`);
  const [unitsSnap, countSnap] = await Promise.all([
    getAggregateFromServer(query(col), { units: sum('totalSold') }),
    getCountFromServer(query(col, where('totalSold', '>', 0))),
  ]);
  return { unitsSold: unitsSnap.data().units || 0, productsSold: countSnap.data().count || 0 };
};

// ─── Internal ────────────────────────────────────────────────────────────────

const searchCustomerProducts = (passedLimit, lastSnap, customer, searchString, override=false) => {
  const custColRef = collection(db, "customers")
  const custDocRef = doc(custColRef, customer)

  return lastSnap
    ? query(
        collection(custDocRef, 'products'),
        where('finishedProcessing', '==', true),
        where('searchableText', 'array-contains', searchString),
        where('hideFromMarketplace', '==', false),
        orderBy('created', 'desc'),
        startAfter(lastSnap),
        limit(passedLimit)
      )
    : query(
        collection(custDocRef, 'products'),
        where('finishedProcessing', '==', true),
        where('hideFromMarketplace', '==', false),
        where('searchableText', 'array-contains', searchString),
        orderBy('created', 'desc'), 
        limit(passedLimit));
};